import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: { bg: "#FEF3C7", fg: "#92400E", label: "New" },
  contacted: { bg: "#DBEAFE", fg: "#1E40AF", label: "Contacted" },
  won: { bg: "#D1FAE5", fg: "#065F46", label: "Won" },
  lost: { bg: "#FEE2E2", fg: "#991B1B", label: "Lost" },
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads", { params: filter === "all" ? {} : { status: filter } });
      setLeads(r.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const counts = leads.reduce((a, l) => { a[l.status] = (a[l.status] || 0) + 1; return a; }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-leads-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-10">
        <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-stone-500 hover:text-stone-900">← Back to admin</Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-2">Custom Website Leads · ₹8,000</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light tracking-tight">Schedule meeting requests</h1>
          </div>
          <div className="text-sm text-stone-600">{leads.length} total{filter !== "all" && ` · ${filter}`}</div>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="leads-filter-bar">
          {["all", "new", "contacted", "won", "lost"].map((k) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`leads-filter-${k}`} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${filter === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-900"}`}>
              {k === "all" ? `All (${leads.length})` : `${STATUS_COLORS[k]?.label} (${counts[k] || 0})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-stone-500">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="border border-dashed border-stone-300 p-16 text-center">
            <div className="font-serif-display text-2xl mb-2">No leads {filter !== "all" && filter}.</div>
            <div className="text-stone-500 text-sm">Custom website lead form submissions will appear here.</div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5">
            {leads.map((l) => {
              const sc = STATUS_COLORS[l.status] || STATUS_COLORS.new;
              const phoneClean = (l.phone || "").replace(/[^0-9]/g, "").slice(-10);
              const wa = `https://wa.me/91${phoneClean}?text=${encodeURIComponent(`Hi ${l.name}, this is the Weddinsite design team. Thanks for your custom website request — when can we hop on a quick call?`)}`;
              return (
                <article key={l.id} className="bg-white border border-stone-200 p-5" data-testid={`lead-${l.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="font-serif-display text-2xl truncate">{l.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mt-1">
                        {new Date(l.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 flex-shrink-0" style={{ background: sc.bg, color: sc.fg }}>{sc.label}</span>
                  </div>

                  <div className="space-y-1.5 text-sm text-stone-700 mb-4">
                    <div>📞 <a href={`tel:+91${l.phone.replace(/[^0-9+]/g,'')}`} className="hover:text-stone-900 font-mono">{l.phone}</a></div>
                    {l.email && <div>✉ <a href={`mailto:${l.email}`} className="hover:text-stone-900">{l.email}</a></div>}
                    {l.wedding_date && <div>💍 Wedding: <strong>{l.wedding_date}</strong></div>}
                    {l.preferred_call_time && <div>⏱ Best time: {l.preferred_call_time}</div>}
                  </div>

                  {l.notes && (
                    <div className="bg-amber-50 border-l-4 border-amber-300 p-3 text-sm text-stone-700 italic mb-4 whitespace-pre-line">"{l.notes}"</div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    <a href={`tel:+91${l.phone.replace(/[^0-9+]/g,'')}`} data-testid={`lead-call-${l.id}`} className="px-3 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em] hover:bg-stone-800">📞 Call</a>
                    <a href={wa} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-${l.id}`} className="px-3 py-2 bg-emerald-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-emerald-700">💬 WhatsApp</a>
                  </div>

                  <div className="flex flex-wrap gap-1 border-t border-stone-100 pt-3">
                    {["new", "contacted", "won", "lost"].map((s) => (
                      <button key={s} onClick={() => setStatus(l.id, s)} disabled={l.status === s} data-testid={`lead-set-${s}-${l.id}`} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border transition-colors ${l.status === s ? "bg-stone-100 text-stone-400 border-stone-200 cursor-default" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"}`}>
                        {STATUS_COLORS[s].label}
                      </button>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
